import { useEffect, useState } from 'react';
import { AlertTriangle, Check, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchNotifications, type OperatorNotification } from '../../lib/api';

interface OperatorInboxProps {
  compact?: boolean;
  refreshKey?: number;
  onAck?: (id: string) => void;
}

function priorityClass(priority: string) {
  switch (priority) {
    case 'urgent':
      return 'border-zt-danger/50 bg-zt-danger/10';
    case 'high':
      return 'border-amber-500/40 bg-amber-500/10';
    default:
      return 'border-zt-border bg-zt-surface';
  }
}

export default function OperatorInbox({ compact, refreshKey = 0, onAck }: OperatorInboxProps) {
  const [items, setItems] = useState<OperatorNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await fetchNotifications();
      if (!cancelled && res.ok && res.data) {
        setItems(res.data.items);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return (
    <div className={compact ? '' : 'rounded-xl border border-zt-border bg-zt-surface p-4'}>
      <div className="flex items-center justify-between gap-2 border-b border-zt-border px-4 py-3">
        <h3 className="text-sm font-medium text-white">Operator inbox</h3>
        <div className="flex items-center gap-3">
          <Link to="/ai?rail=inbox" className="text-xs text-zt-primary hover:underline">
            AI Console
          </Link>
          {!compact && (
            <Link to="/approvals" className="text-xs text-zt-primary hover:underline">
              Approvals
            </Link>
          )}
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto p-2">
        {loading && <p className="p-3 text-sm text-zt-muted">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="p-3 text-sm text-zt-muted">No notifications — you&apos;re all caught up.</p>
        )}
        <ul className="space-y-2">
          {items.map((n) => (
            <li
              key={n.id}
              className={`rounded-lg border p-3 ${priorityClass(n.priority)} ${
                n.acknowledged ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-400" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-100">{n.title}</p>
                  <p className="mt-1 text-xs text-zt-muted">{n.body}</p>
                  {n.steps.length > 0 && (
                    <ol className="mt-2 list-decimal space-y-1 pl-4 text-xs text-slate-300">
                      {n.steps.map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ol>
                  )}
                  {n.relatedApprovalId && (
                    <Link
                      to="/approvals"
                      className="mt-2 inline-flex items-center gap-1 text-xs text-zt-primary hover:underline"
                    >
                      <ExternalLink size={12} /> View approval
                    </Link>
                  )}
                </div>
                {!n.acknowledged && onAck && (
                  <button
                    type="button"
                    onClick={() => onAck(n.id)}
                    className="shrink-0 rounded p-1 text-zt-muted hover:bg-zt-bg hover:text-emerald-400"
                    aria-label="Acknowledge"
                  >
                    <Check size={16} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
